import { FUSEAU_PAR_DEFAUT, jourDansFuseau, type CustomFieldValues } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { DEMO_PRODUITS, DEMO_PROTOCOLES } from '../catalogue/demo-dataset.js';

/**
 * LE JEU DE DEMONSTRATION de l'app Consultations : la journee d'une clinique
 * veterinaire belge, son historique sur dix-huit mois et les taches de ses
 * dossiers. Des histoires inventees, sur les animaux de la demo de Patients et
 * les produits de la demo du Catalogue (retrouves par leur `demoKey` : sans
 * eux, rien n'est cree et le message le dit. L'ordre est Catalogue, Patients,
 * Consultations).
 *
 * AUJOURD'HUI, dix consultations a TOUTES les etapes du parcours, de 8 h 30 a
 * 18 h : deux payees, une a facturer, deux en consultation, deux en salle
 * d'attente, trois prevues. Quatre autres sont prevues dans la semaine. Six des
 * dix du jour sont a la personne connectee (« Mes consultations »), les quatre
 * autres a un collegue.
 *
 * L'HISTORIQUE (une vingtaine de visites payees) dessine pour chaque animal une
 * courbe de poids et un carnet de vaccination vrai : a jour, bientot, en retard
 * ou jamais fait. Rex, le chien senior cardiaque, a huit visites sur dix-sept
 * mois : son dossier montre tout.
 *
 * CE QUE CHAQUE VISITE PORTE, quand elle est passee ou en cours : son compte
 * rendu, ses soins (un par ligne de protocole quand elle en vient un), son
 * EXAMEN CLINIQUE (une `inspection` `examen` en liste pour le chien et le chat,
 * en texte pour les autres especes), ses CONSTANTES (une `measurement` par
 * chiffre) et ses examens complementaires.
 *
 * DEUX MODELES DE CONSULTATION (famille `templates`, « Premiere visite chiot »
 * et « Vaccin annuel chat ») nourrissent le geste « Creer depuis un modele » de
 * la porte Planning : un motif, et des lignes de soin liees par
 * `customFields.consultationId` que `instantiate` rejoue, enfants compris.
 *
 * Memes regles que `../taches/demo-dataset.ts` : tout passe par `@taki/sdk`,
 * IDEMPOTENT par `customFields.demoKey` (`co-*`), aucune suppression, AUCUNE
 * DATE FIGEE : tout se calcule depuis aujourd'hui, dans le fuseau de l'org
 * (`sdk.timezone`). Une date PURE est une chaine `YYYY-MM-DD`, jamais un
 * Timestamp. Les valeurs sont les valeurs STOCKEES des catalogues.
 */

const TYPE_CONSULTATION = 'consultation';
const TYPE_SOIN = 'soin';
const TYPE_TACHE = 'dossier';

/** Les deux personnes de l'emulateur local (`scripts/compte-de-test.mjs`), par adresse. */
const MOI = 'test@taki.lu';
const COLLEGUE = 'membre@taki.lu';

/** Ce que l'import a fait, rendu au hub Parametres. */
export interface DemoImportResult {
  created: number;
  skipped: number;
  message: string;
}

export type Etape = 'prevue' | 'salleAttente' | 'enConsultation' | 'aFacturer' | 'payee';
type Veterinaire = 'moi' | 'collegue';

/** Un soin : un produit, ou un protocole entier (une ligne de soin par ligne du protocole). */
export type DemoSoin =
  | { produit: string; quantite?: number; rappelJours?: number }
  | { protocole: string; rappelJours?: number };

export interface DemoExamen {
  /** Les points anormaux, par clef de point : choix rapides et note. */
  anormaux?: Readonly<Record<string, { choix: readonly string[]; note?: string }>>;
  /** Les points (ou systemes) non examines : le verdict est alors « incomplet ». */
  nonExamines?: readonly string[];
  /** Les constantes : `metric` vers le nombre. */
  mesures: Readonly<Record<string, number>>;
}

export interface DemoConsultation {
  demoKey: string;
  /** La `demoKey` de l'animal dans la demo de Patients. */
  animal: string;
  /** Jours depuis aujourd'hui (negatif = passe), et heure locale de l'org. */
  jour: number;
  heure: number;
  minute: number;
  reason: string;
  stage: Etape;
  vet: Veterinaire;
  description?: string;
  /** Le compte rendu : les clefs du sac, telles que declarees. */
  compteRendu?: Readonly<Record<string, string | readonly string[]>>;
  /** La conclusion de chaque examen complementaire (`extraExams`) de la visite. */
  conclusions?: Readonly<Record<string, string>>;
  soins?: ReadonlyArray<DemoSoin>;
  examen?: DemoExamen;
}

const CONSULTATION = { produit: 'pa-r10' } as const;

export const DEMO_CONSULTATIONS: ReadonlyArray<DemoConsultation> = [
  /* ------------------------------------------------------------------ */
  /* AUJOURD'HUI : toutes les etapes du parcours                         */
  /* ------------------------------------------------------------------ */
  { demoKey: 'co-t1', animal: 'pa-a3', jour: 0, heure: 8, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'moi',
    description: 'Chaleurs, projet de stérilisation',
    compteRendu: { anamnesis: 'Première chaleur il y a trois semaines. Les maîtres envisagent la stérilisation.', diagnosis: 'Chienne en bonne santé.', therapy: 'Stérilisation à programmer deux à trois mois après les chaleurs.', followUp: 'Contrôle suivant' },
    soins: [CONSULTATION],
    examen: { mesures: { weight: 26.1, temperature: 38.5, heartRate: 96, capillaryRefillTime: 1.5 } } },
  { demoKey: 'co-t2', animal: 'pa-a2', jour: 0, heure: 9, minute: 15, reason: 'Contrôle', stage: 'payee', vet: 'collegue',
    description: 'Suivi de la toux',
    compteRendu: { anamnesis: 'Ne tousse plus depuis cinq jours, mange bien.', diagnosis: 'Bronchite guérie.', therapy: 'Vermifuge donné. Rappel de vaccin à prévoir, il est en retard.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { produit: 'pa-r6', quantite: 1 }],
    examen: { mesures: { weight: 4.2, temperature: 38.6, heartRate: 172, respiratoryRate: 28 } } },
  { demoKey: 'co-t3', animal: 'pa-a4', jour: 0, heure: 10, minute: 0, reason: 'Maladie', stage: 'aFacturer', vet: 'moi',
    description: 'Vomissements depuis deux jours',
    compteRendu: { anamnesis: 'Vomit après chaque repas depuis deux jours, un peu abattu.', exam: 'Abdomen souple mais sensible à la palpation haute.', extraExams: ['Prise de sang', 'Échographie'], diagnosis: 'Gastrite probable, bilan sanguin sans anomalie.', therapy: 'Diète douze heures, puis alimentation digestive pendant trois jours.', followUp: 'Contrôle suivant', prognosis: 'Bon.' },
    conclusions: { 'Prise de sang': 'Numération et biochimie normales.', 'Échographie': 'Paroi gastrique épaissie, pas de corps étranger.' },
    soins: [CONSULTATION, { produit: 'ca-a7', quantite: 1 }, { protocole: 'ca-p1' }],
    examen: { anormaux: { etatGeneral: { choix: ['Abattu'] }, abdomen: { choix: ['Douleur'], note: 'Sensibilité haute' } }, mesures: { weight: 5.8, temperature: 39.1, heartRate: 180, respiratoryRate: 32 } } },
  { demoKey: 'co-t4', animal: 'pa-a9', jour: 0, heure: 11, minute: 0, reason: 'Maladie', stage: 'enConsultation', vet: 'moi',
    description: 'Démangeaisons et oreilles',
    compteRendu: { anamnesis: 'Se gratte depuis trois semaines, surtout les oreilles et les pattes. Déjà suivi pour une allergie.' },
    soins: [CONSULTATION],
    examen: {
      anormaux: { peau: { choix: ['Prurit'], note: 'Plis inguinaux, érythème' }, oreilleDroite: { choix: ['Conduit rouge', 'Cérumen ++'] }, oreilleGauche: { choix: ['Conduit rouge'] } },
      nonExamines: ['ganglions', 'abdomen', 'locomoteur', 'neuro', 'urogenital'],
      mesures: { weight: 18.4, temperature: 38.7, heartRate: 100 },
    } },
  { demoKey: 'co-t5', animal: 'pa-a12', jour: 0, heure: 11, minute: 45, reason: 'Vaccination', stage: 'enConsultation', vet: 'collegue',
    description: 'Primo-vaccination',
    compteRendu: { anamnesis: 'Adopté il y a une semaine, mange et joue bien.' },
    soins: [{ protocole: 'ca-p3', rappelJours: 21 }],
    examen: { nonExamines: ['ganglions', 'locomoteur', 'neuro', 'urogenital'], mesures: { weight: 1.6, temperature: 38.8, heartRate: 190, respiratoryRate: 40 } } },
  { demoKey: 'co-t6', animal: 'pa-a1', jour: 0, heure: 14, minute: 0, reason: 'Contrôle', stage: 'salleAttente', vet: 'moi',
    description: 'Suivi cardiaque et toux nocturne',
    compteRendu: { anamnesis: 'Tousse la nuit depuis dix jours, essoufflé en promenade. Mêmes consignes depuis la dernière visite.' } },
  { demoKey: 'co-t7', animal: 'pa-a10', jour: 0, heure: 14, minute: 45, reason: 'Contrôle', stage: 'salleAttente', vet: 'collegue',
    description: 'Suivi du diabète',
    compteRendu: { anamnesis: 'Boit beaucoup, mange bien. Insuline matin et soir, bien tolérée.' } },
  { demoKey: 'co-t8', animal: 'pa-a6', jour: 0, heure: 15, minute: 30, reason: 'Blessure', stage: 'prevue', vet: 'moi', description: 'Plaie au boulet postérieur droit' },
  { demoKey: 'co-t9', animal: 'pa-a7', jour: 0, heure: 16, minute: 45, reason: 'Maladie', stage: 'prevue', vet: 'moi', description: 'Ne mange plus depuis hier' },
  { demoKey: 'co-t10', animal: 'pa-a5', jour: 0, heure: 18, minute: 0, reason: 'Contrôle', stage: 'prevue', vet: 'collegue', description: 'Contrôle des dents' },

  /* ------------------------------------------------------------------ */
  /* DEMAIN ET DANS LA SEMAINE : prevues                                 */
  /* ------------------------------------------------------------------ */
  { demoKey: 'co-f1', animal: 'pa-a11', jour: 1, heure: 9, minute: 30, reason: 'Vaccination', stage: 'prevue', vet: 'collegue', description: 'Rappel annuel' },
  { demoKey: 'co-f2', animal: 'pa-a1', jour: 2, heure: 10, minute: 0, reason: 'Contrôle', stage: 'prevue', vet: 'moi', description: 'Échographie cardiaque' },
  { demoKey: 'co-f3', animal: 'pa-a10', jour: 3, heure: 11, minute: 15, reason: 'Contrôle', stage: 'prevue', vet: 'collegue', description: 'Courbe de glycémie' },
  { demoKey: 'co-f4', animal: 'pa-a4', jour: 5, heure: 15, minute: 0, reason: 'Contrôle', stage: 'prevue', vet: 'moi', description: 'Contrôle digestif' },

  /* ------------------------------------------------------------------ */
  /* REX, le chien senior cardiaque : huit visites sur dix-sept mois     */
  /* ------------------------------------------------------------------ */
  { demoKey: 'co-r1', animal: 'pa-a1', jour: -510, heure: 9, minute: 30, reason: 'Vaccination', stage: 'payee', vet: 'moi',
    description: 'Vaccins annuels',
    compteRendu: { anamnesis: 'Rien à signaler.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { protocole: 'ca-p2' }],
    examen: { mesures: { weight: 31.2, temperature: 38.5, heartRate: 94 } } },
  { demoKey: 'co-r2', animal: 'pa-a1', jour: -420, heure: 11, minute: 0, reason: 'Maladie', stage: 'payee', vet: 'moi',
    description: 'Oreille qui gratte',
    compteRendu: { anamnesis: 'Secoue la tête depuis trois jours.', diagnosis: 'Otite externe gauche.', therapy: 'Nettoyant auriculaire et gouttes deux fois par jour, dix jours.' },
    soins: [CONSULTATION],
    examen: { anormaux: { oreilleGauche: { choix: ['Conduit rouge', 'Cérumen ++', 'Odeur'] } }, nonExamines: ['neuro', 'urogenital'], mesures: { weight: 31, temperature: 38.9, heartRate: 100 } } },
  { demoKey: 'co-r3', animal: 'pa-a1', jour: -330, heure: 17, minute: 30, reason: 'Blessure', stage: 'payee', vet: 'moi',
    description: 'Boiterie arrière gauche',
    compteRendu: { anamnesis: 'Boite depuis hier soir après une course au parc.', diagnosis: 'Entorse du grasset gauche.', therapy: 'Anti-inflammatoire cinq jours, repos strict deux semaines.', prognosis: 'Bon.' },
    soins: [CONSULTATION, { produit: 'ca-m3', quantite: 10 }],
    examen: { anormaux: { locomoteur: { choix: ['Boiterie', 'Douleur articulaire'], note: 'Postérieur gauche, appui partiel' } }, mesures: { weight: 30.4, temperature: 38.7, heartRate: 104 } } },
  { demoKey: 'co-r4', animal: 'pa-a1', jour: -250, heure: 15, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'collegue',
    description: 'Démangeaisons',
    compteRendu: { anamnesis: 'Se gratte depuis une semaine.', diagnosis: 'Dermatite parasitaire probable.', therapy: 'Vermifuge et antiparasitaire.', followUp: 'Contrôle suivant' },
    soins: [CONSULTATION, { produit: 'ca-m6', quantite: 1 }, { produit: 'pa-r6', quantite: 1 }],
    examen: { anormaux: { peau: { choix: ['Prurit', 'Parasites'], note: 'Puces, dos et base de la queue' } }, mesures: { weight: 30.1, temperature: 38.6, heartRate: 108 } } },
  { demoKey: 'co-r5', animal: 'pa-a1', jour: -170, heure: 10, minute: 0, reason: 'Contrôle', stage: 'payee', vet: 'moi',
    description: 'Fatigue à l’effort',
    compteRendu: { anamnesis: 'Se fatigue plus vite en promenade.', extraExams: ['Échographie'], diagnosis: 'Souffle mitral débutant (2/6).', therapy: 'Pas de traitement pour l’instant. Contrôle régulier.', prognosis: 'Bon, à suivre.', followUp: 'Contrôle suivant' },
    conclusions: { 'Échographie': 'Oreillette gauche légèrement dilatée, insuffisance mitrale légère (stade B1).' },
    soins: [CONSULTATION, { produit: 'ca-a4', quantite: 1 }, { produit: 'ca-c3', quantite: 2 }],
    examen: { anormaux: { auscultationCardiaque: { choix: ['Souffle'], note: 'Souffle 2/6 apex gauche' } }, mesures: { weight: 29.6, temperature: 38.4, heartRate: 118, respiratoryRate: 28 } } },
  { demoKey: 'co-r6', animal: 'pa-a1', jour: -95, heure: 9, minute: 30, reason: 'Vaccination', stage: 'payee', vet: 'moi',
    description: 'Rappel annuel',
    compteRendu: { anamnesis: 'Rien de nouveau, toujours un peu essoufflé.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { protocole: 'ca-p2' }],
    examen: { anormaux: { auscultationCardiaque: { choix: ['Souffle'], note: 'Souffle 2/6, stable' } }, mesures: { weight: 29.2, temperature: 38.5, heartRate: 112 } } },
  { demoKey: 'co-r7', animal: 'pa-a1', jour: -50, heure: 14, minute: 0, reason: 'Contrôle', stage: 'payee', vet: 'moi',
    description: 'Bilan cardiaque',
    compteRendu: { anamnesis: 'Plus fatigable, tousse en fin de promenade.', extraExams: ['Prise de sang', 'Radiographie'], diagnosis: 'Insuffisance mitrale stade B2.', therapy: 'Traitement cardiaque à débuter, contrôle dans un mois.', prognosis: 'Réservé à long terme.', followUp: 'Contrôle suivant' },
    conclusions: { 'Prise de sang': 'Bilan rénal et hépatique normaux.', 'Radiographie': 'Silhouette cardiaque agrandie, poumons clairs.' },
    soins: [CONSULTATION, { produit: 'ca-a7', quantite: 1 }, { produit: 'ca-a8', quantite: 1 }],
    examen: { anormaux: { auscultationCardiaque: { choix: ['Souffle'], note: 'Souffle 3/6' } }, mesures: { weight: 28.9, temperature: 38.4, heartRate: 124, respiratoryRate: 30 } } },
  { demoKey: 'co-r8', animal: 'pa-a1', jour: -21, heure: 16, minute: 0, reason: 'Maladie', stage: 'payee', vet: 'moi',
    description: 'Toux nocturne',
    compteRendu: { anamnesis: 'Tousse la nuit depuis quelques jours.', diagnosis: 'Toux d’origine cardiaque, pas d’infection.', therapy: 'Même traitement, surveiller la fréquence respiratoire au repos.', followUp: 'Contrôle suivant' },
    soins: [CONSULTATION],
    examen: { anormaux: { auscultationCardiaque: { choix: ['Souffle'], note: 'Souffle 3/6' }, modeRespiratoire: { choix: ['Toux'] } }, mesures: { weight: 28.6, temperature: 38.5, heartRate: 128, respiratoryRate: 32 } } },

  /* ------------------------------------------------------------------ */
  /* LES AUTRES HISTORIQUES : un carnet vrai par animal                  */
  /* ------------------------------------------------------------------ */
  /* Billie : vaccins bientot dus, vermifuge bientot du. */
  { demoKey: 'co-b1', animal: 'pa-a3', jour: -350, heure: 10, minute: 0, reason: 'Vaccination', stage: 'payee', vet: 'moi',
    description: 'Vaccins annuels',
    compteRendu: { anamnesis: 'Rien à signaler, grandit bien.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { protocole: 'ca-p2' }, { produit: 'ca-v7', quantite: 1 }],
    examen: { mesures: { weight: 23.4, temperature: 38.7, heartRate: 108 } } },
  { demoKey: 'co-b2', animal: 'pa-a3', jour: -200, heure: 15, minute: 0, reason: 'Maladie', stage: 'payee', vet: 'collegue',
    description: 'Diarrhée',
    compteRendu: { anamnesis: 'Selles molles depuis deux jours après avoir volé un reste de repas.', diagnosis: 'Gastro-entérite alimentaire.', therapy: 'Diète puis alimentation digestive.', prognosis: 'Bon.' },
    soins: [CONSULTATION],
    examen: { anormaux: { abdomen: { choix: ['Douleur'], note: 'Sensibilité diffuse' } }, mesures: { weight: 24.8, temperature: 38.9, heartRate: 112 } } },
  { demoKey: 'co-b3', animal: 'pa-a3', jour: -70, heure: 16, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'moi',
    description: 'Vermifugation',
    compteRendu: { anamnesis: 'Rien à signaler.' },
    soins: [CONSULTATION, { produit: 'pa-r6', quantite: 2 }],
    examen: { mesures: { weight: 25.6, temperature: 38.5, heartRate: 98 } } },
  /* Mimi : vaccins en retard. */
  { demoKey: 'co-m1', animal: 'pa-a2', jour: -400, heure: 16, minute: 0, reason: 'Vaccination', stage: 'payee', vet: 'collegue',
    description: 'Vaccin annuel',
    compteRendu: { anamnesis: 'Rien à signaler.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { produit: 'pa-r3', quantite: 1 }, { produit: 'ca-v8', quantite: 1 }],
    examen: { anormaux: { bouche: { choix: ['Tartre'], note: 'Détartrage à proposer' } }, mesures: { weight: 3.8, temperature: 38.6, heartRate: 168 } } },
  { demoKey: 'co-m2', animal: 'pa-a2', jour: -12, heure: 9, minute: 45, reason: 'Maladie', stage: 'payee', vet: 'collegue',
    description: 'Toux',
    compteRendu: { anamnesis: 'Tousse depuis trois jours, mange bien.', exam: 'Pas de fièvre. Auscultation : léger sifflement.', extraExams: ['Radiographie'], diagnosis: 'Bronchite légère.', therapy: 'Anti-inflammatoire et repos, sept jours.', followUp: 'Résultats à communiquer', prognosis: 'Bon.' },
    conclusions: { 'Radiographie': 'Motif bronchique léger, pas de masse.' },
    soins: [CONSULTATION, { produit: 'ca-a8', quantite: 1 }, { produit: 'ca-m11', quantite: 1 }],
    examen: { anormaux: { auscultationRespiratoire: { choix: ['Sifflements'] }, modeRespiratoire: { choix: ['Toux'] } }, mesures: { weight: 4.1, temperature: 38.9, heartRate: 176, respiratoryRate: 36 } } },
  /* Luna : chatte diabetique de quatorze ans, qui maigrit. */
  { demoKey: 'co-l1', animal: 'pa-a10', jour: -300, heure: 10, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'collegue',
    description: 'Bilan senior',
    compteRendu: { anamnesis: 'Boit et urine beaucoup depuis un mois.', extraExams: ['Prise de sang', 'Analyse d\'urine'], diagnosis: 'Diabète sucré.', therapy: 'Insuline matin et soir, alimentation adaptée.', prognosis: 'Réservé, bon si bien stabilisée.', followUp: 'Contrôle suivant' },
    conclusions: { 'Prise de sang': 'Glycémie et fructosamine élevées.', 'Analyse d\'urine': 'Glucosurie, pas d’infection.' },
    soins: [CONSULTATION, { produit: 'ca-a7', quantite: 1 }, { produit: 'pa-r3', quantite: 1 }],
    examen: { anormaux: { etatGeneral: { choix: ['Déshydraté'] }, bouche: { choix: ['Gingivite', 'Tartre'] } }, mesures: { weight: 4.7, temperature: 38.4, heartRate: 190, respiratoryRate: 30 } } },
  { demoKey: 'co-l2', animal: 'pa-a10', jour: -150, heure: 14, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'collegue',
    description: 'Suivi du diabète',
    compteRendu: { anamnesis: 'Boit moins, bon appétit. Insuline bien tolérée.', therapy: 'Même dose d’insuline.', followUp: 'Contrôle suivant' },
    soins: [CONSULTATION],
    examen: { anormaux: { bouche: { choix: ['Gingivite'] } }, mesures: { weight: 4.5, temperature: 38.3, heartRate: 186 } } },
  { demoKey: 'co-l3', animal: 'pa-a10', jour: -40, heure: 16, minute: 15, reason: 'Contrôle', stage: 'payee', vet: 'collegue',
    description: 'Suivi du diabète',
    compteRendu: { anamnesis: 'Un peu moins de poids, appétit correct.', therapy: 'Même dose, à revoir dans un mois.', followUp: 'Contrôle suivant' },
    soins: [CONSULTATION, { produit: 'pa-r6', quantite: 1 }],
    examen: { anormaux: { etatCorporel: { choix: ['Maigre'] } }, mesures: { weight: 4.4, temperature: 38.4, heartRate: 188 } } },
  /* Jazz : a jour. */
  { demoKey: 'co-j1', animal: 'pa-a9', jour: -200, heure: 9, minute: 0, reason: 'Vaccination', stage: 'payee', vet: 'moi',
    description: 'Vaccins annuels',
    compteRendu: { anamnesis: 'Rien à signaler, mais très craintif.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { protocole: 'ca-p2' }],
    examen: { anormaux: { comportement: { choix: ['Craintif'] } }, mesures: { weight: 18, temperature: 38.6, heartRate: 104 } } },
  { demoKey: 'co-j2', animal: 'pa-a9', jour: -40, heure: 13, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'collegue',
    description: 'Antiparasitaire',
    compteRendu: { anamnesis: 'Se gratte un peu moins qu’au printemps.' },
    soins: [CONSULTATION, { produit: 'ca-m6', quantite: 1 }],
    examen: { anormaux: { peau: { choix: ['Prurit'] } }, mesures: { weight: 18.3, temperature: 38.6, heartRate: 100 } } },
  /* Gaston et Orage, les chevaux : l'examen s'ecrit en texte. */
  { demoKey: 'co-g1', animal: 'pa-a6', jour: -200, heure: 10, minute: 0, reason: 'Vaccination', stage: 'payee', vet: 'moi',
    description: 'Rappel annuel',
    compteRendu: { anamnesis: 'Rien à signaler.', exam: 'Auscultation normale, muqueuses roses, aplombs corrects. Gaston reste nerveux à la prise de sang.', followUp: 'Rappel de vaccin' },
    soins: [{ protocole: 'ca-p5' }],
    examen: { mesures: { weight: 784, temperature: 37.7, heartRate: 36, respiratoryRate: 14 } } },
  { demoKey: 'co-g2', animal: 'pa-a6', jour: -90, heure: 11, minute: 0, reason: 'Contrôle', stage: 'payee', vet: 'moi',
    description: 'Perte de poids',
    compteRendu: { anamnesis: 'Maigrit un peu depuis l’hiver, mange bien.', exam: 'Dents en bon état, état corporel 4/9.', therapy: 'Vermifugation et ration à réajuster.', followUp: 'Contrôle suivant' },
    soins: [{ produit: 'ca-a10', quantite: 1 }, { produit: 'pa-r7', quantite: 1 }],
    examen: { mesures: { weight: 779, temperature: 37.8, heartRate: 38 } } },
  { demoKey: 'co-o1', animal: 'pa-a11', jour: -170, heure: 10, minute: 30, reason: 'Vaccination', stage: 'payee', vet: 'collegue',
    description: 'Rappel annuel',
    compteRendu: { anamnesis: 'Rien à signaler.', exam: 'Cheval en pleine forme, auscultation normale.', followUp: 'Rappel de vaccin' },
    soins: [{ protocole: 'ca-p5' }],
    examen: { mesures: { weight: 541, temperature: 37.6, heartRate: 38, respiratoryRate: 12 } } },
  /* Pixel, Caramel, Noisette. */
  { demoKey: 'co-p1', animal: 'pa-a4', jour: -350, heure: 15, minute: 30, reason: 'Vaccination', stage: 'payee', vet: 'moi',
    description: 'Primo-vaccination',
    compteRendu: { anamnesis: 'Chaton adopté en bonne santé.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { produit: 'pa-r3', quantite: 1 }, { produit: 'pa-r6', quantite: 1 }],
    examen: { anormaux: { comportement: { choix: ['Craintif'] } }, mesures: { weight: 4.1, temperature: 38.7, heartRate: 188 } } },
  { demoKey: 'co-p2', animal: 'pa-a4', jour: -150, heure: 11, minute: 30, reason: 'Contrôle', stage: 'payee', vet: 'moi',
    description: 'Contrôle de croissance',
    compteRendu: { anamnesis: 'Mange bien, joue beaucoup.' },
    soins: [CONSULTATION],
    examen: { mesures: { weight: 5.2, temperature: 38.6, heartRate: 180 } } },
  { demoKey: 'co-c1', animal: 'pa-a5', jour: -300, heure: 14, minute: 0, reason: 'Vaccination', stage: 'payee', vet: 'collegue',
    description: 'Vaccin annuel',
    compteRendu: { anamnesis: 'Rien à signaler.', exam: 'Dents correctes, pas de jetage, oreilles propres.', followUp: 'Rappel de vaccin' },
    soins: [CONSULTATION, { produit: 'pa-r4', quantite: 1 }, { produit: 'ca-m19', quantite: 1 }],
    examen: { mesures: { weight: 2, temperature: 38.9, heartRate: 210 } } },
  { demoKey: 'co-n1', animal: 'pa-a7', jour: -150, heure: 9, minute: 45, reason: 'Maladie', stage: 'payee', vet: 'moi',
    description: 'Dents trop longues',
    compteRendu: { anamnesis: 'Mange moins de foin depuis une semaine.', exam: 'Incisives trop longues, pas d’abcès.', diagnosis: 'Malocclusion légère.', therapy: 'Dents limées, foin à volonté.', prognosis: 'Bon.' },
    soins: [CONSULTATION],
    examen: { mesures: { weight: 0.98 } } },
];

/* ------------------------------------------------------------------ */
/* Les taches des dossiers                                             */
/* ------------------------------------------------------------------ */

export interface DemoTache {
  demoKey: string;
  libelle: string;
  /** `aFaire`, `enCours`, `termine`. */
  status: string;
  vet: Veterinaire;
  animal: string;
  consultation: string;
  /** Echeance en jours depuis aujourd'hui (negatif = en retard). */
  echeance: number;
}

export const DEMO_TACHES: ReadonlyArray<DemoTache> = [
  { demoKey: 'co-ta1', libelle: 'Rappeler Mme Lambert : résultats de la toux de Rex', status: 'aFaire', vet: 'moi', animal: 'pa-a1', consultation: 'co-r8', echeance: -2 },
  { demoKey: 'co-ta2', libelle: 'Commander l’insuline de Luna', status: 'aFaire', vet: 'collegue', animal: 'pa-a10', consultation: 'co-l3', echeance: -1 },
  { demoKey: 'co-ta3', libelle: 'Envoyer le compte rendu d’échographie de Pixel', status: 'aFaire', vet: 'moi', animal: 'pa-a4', consultation: 'co-t3', echeance: 1 },
  { demoKey: 'co-ta4', libelle: 'Programmer la stérilisation de Billie', status: 'aFaire', vet: 'moi', animal: 'pa-a3', consultation: 'co-t1', echeance: 14 },
  { demoKey: 'co-ta5', libelle: 'Revoir les oreilles de Jazz dans dix jours', status: 'aFaire', vet: 'moi', animal: 'pa-a9', consultation: 'co-t4', echeance: 10 },
  { demoKey: 'co-ta6', libelle: 'Planifier le rappel de Pépito dans trois semaines', status: 'aFaire', vet: 'collegue', animal: 'pa-a12', consultation: 'co-t5', echeance: 21 },
  { demoKey: 'co-ta7', libelle: 'Préparer le matériel pour la visite d’Orage', status: 'enCours', vet: 'collegue', animal: 'pa-a11', consultation: 'co-f1', echeance: 1 },
  { demoKey: 'co-ta8', libelle: 'Mettre à jour le traitement cardiaque de Rex', status: 'enCours', vet: 'moi', animal: 'pa-a1', consultation: 'co-t6', echeance: 1 },
  { demoKey: 'co-ta9', libelle: 'Facturer la consultation de Mimi', status: 'termine', vet: 'collegue', animal: 'pa-a2', consultation: 'co-t2', echeance: 0 },
];

/** Le nombre total de lignes principales que ce jeu pose, cite par le hub Parametres (les soins, examens et constantes s'y ajoutent). */
export const DEMO_TOTAL = DEMO_CONSULTATIONS.length + DEMO_TACHES.length;

/* ------------------------------------------------------------------ */
/* L'examen clinique en liste                                          */
/* ------------------------------------------------------------------ */

/**
 * LES POINTS DE L'EXAMEN EN LISTE, chien et chat : `[systeme, clef, libelle]`.
 * Le format d'un point stocke dans `inspection.checkpoints` est celui du
 * dossier de l'animal (`../patients/canvas/dossier/examen.ts`) : clef, systeme,
 * libelle, etat, choix, note.
 */
export const POINTS_DE_L_EXAMEN: ReadonlyArray<readonly [string, string, string]> = [
  ['general', 'etatGeneral', 'État général'],
  ['general', 'comportement', 'Comportement'],
  ['general', 'etatCorporel', 'État corporel'],
  ['cardio', 'auscultationCardiaque', 'Auscultation'],
  ['cardio', 'pouls', 'Pouls'],
  ['respiratoire', 'auscultationRespiratoire', 'Auscultation'],
  ['respiratoire', 'modeRespiratoire', 'Mode respiratoire'],
  ['muqueuses', 'couleurMuqueuses', 'Couleur'],
  ['muqueuses', 'hydratation', 'Hydratation'],
  ['ganglions', 'ganglions', 'Palpation'],
  ['yeux', 'oeilDroit', 'Droit'],
  ['yeux', 'oeilGauche', 'Gauche'],
  ['oreilles', 'oreilleDroite', 'Droite'],
  ['oreilles', 'oreilleGauche', 'Gauche'],
  ['bouche', 'bouche', 'Bouche et dents'],
  ['peau', 'peau', 'Peau et pelage'],
  ['abdomen', 'abdomen', 'Palpation'],
  ['locomoteur', 'locomoteur', 'Locomoteur'],
  ['neuro', 'neuro', 'Neurologique'],
  ['urogenital', 'urogenital', 'Uro-génital'],
];

/** Les unites des constantes (`metric` vers unite). */
export const UNITES_DES_CONSTANTES: Readonly<Record<string, string>> = {
  weight: 'kg',
  temperature: '°C',
  heartRate: '/min',
  respiratoryRate: '/min',
  capillaryRefillTime: 's',
};

/** Les especes qui ont un examen en liste ; les autres s'ecrivent en texte. */
const ESPECES_EN_LISTE: readonly string[] = ['Chien', 'Chat'];

export interface PointStocke {
  cle: string;
  systeme: string;
  libelle: string;
  etat: 'normal' | 'anormal' | 'nonExamine';
  choix: string[];
  note: string;
}

/** Les points de l'examen : tout normal, sauf les anormaux et les non examines. */
export function pointsDe(examen: DemoExamen): PointStocke[] {
  return POINTS_DE_L_EXAMEN.map(([systeme, cle, libelle]) => {
    const anormal = examen.anormaux?.[cle];
    if (anormal !== undefined) return { cle, systeme, libelle, etat: 'anormal', choix: [...anormal.choix], note: anormal.note ?? '' };
    const nonExamine = examen.nonExamines?.includes(cle) === true || examen.nonExamines?.includes(systeme) === true;
    return { cle, systeme, libelle, etat: nonExamine ? 'nonExamine' : 'normal', choix: [], note: '' };
  });
}

/** Le verdict : anormal des qu'un point l'est, incomplet s'il en reste un non examine, normal sinon. */
export function verdictDe(points: ReadonlyArray<PointStocke>): 'normal' | 'anormal' | 'incomplet' {
  if (points.some((p) => p.etat === 'anormal')) return 'anormal';
  if (points.some((p) => p.etat === 'nonExamine')) return 'incomplet';
  return 'normal';
}

/* ------------------------------------------------------------------ */
/* Les dates, dans le fuseau de l'org                                  */
/* ------------------------------------------------------------------ */

/** Un jour pur decale de `jours` jours. */
export function ajouterJours(jour: string, jours: number): string {
  const [annee, mois, numero] = jour.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(annee, mois - 1, numero + jours)).toISOString().slice(0, 10);
}

/** Le jour d'une visite : jamais un dimanche (la veille de la clinique ouverte, ou le lendemain pour le futur). */
/**
 * LES HEURES DU JOUR, COHERENTES AVEC L'INSTANT DE L'IMPORT. Une consultation
 * du jour deja commencee (en salle d'attente, en consultation, a facturer,
 * payee) a eu lieu AVANT l'import ; une consultation prevue a lieu APRES.
 * Chacune garde son heure prevue quand elle tombe du bon cote, sinon elle se
 * recale : les passees en remontant de 20 minutes en 20 minutes avant
 * l'import (les payees d'abord), les prevues de 30 minutes en 30 minutes apres.
 * Une demo importee a 8 h 27 n'a donc ni consultation payee a 17 h, ni
 * cloture a 8 h 55.
 */
export function heuresDuJour(
  items: readonly DemoConsultation[],
  aujourdhui: string,
  fuseau: string,
  maintenant: Date,
): Map<string, Date> {
  const ordre = ['payee', 'aFacturer', 'enConsultation', 'salleAttente'];
  const duJour = items.filter((item) => item.jour === 0);
  const prevu = (item: DemoConsultation): Date => instantLocal(aujourdhui, item.heure, item.minute, fuseau);
  const passees = duJour
    .filter((item) => item.stage !== 'prevue')
    .sort((a, b) => ordre.indexOf(a.stage) - ordre.indexOf(b.stage) || prevu(a).getTime() - prevu(b).getTime());
  const prevues = duJour.filter((item) => item.stage === 'prevue').sort((a, b) => prevu(a).getTime() - prevu(b).getTime());
  const heures = new Map<string, Date>();
  const minute = 60_000;
  passees.forEach((item, rang) => {
    const limite = maintenant.getTime() - (passees.length - rang) * 20 * minute;
    heures.set(item.demoKey, new Date(Math.min(prevu(item).getTime(), limite)));
  });
  /* Une prevue reste AUJOURD'HUI (Ma journee la montre) : au plus tard 23 h 50. */
  const finDuJour = instantLocal(aujourdhui, 23, 50, fuseau).getTime();
  prevues.forEach((item, rang) => {
    const plancher = maintenant.getTime() + (rang + 1) * 30 * minute;
    heures.set(item.demoKey, new Date(Math.min(Math.max(prevu(item).getTime(), plancher), Math.max(finDuJour, maintenant.getTime() + minute))));
  });
  return heures;
}

/** La cloture : 25 minutes apres le debut, jamais apres l'instant de l'import. */
export function clotureDe(quand: Date, maintenant: Date): Date {
  const fin = Math.min(quand.getTime() + 25 * 60_000, maintenant.getTime() - 60_000);
  return new Date(Math.max(fin, quand.getTime()));
}

export function jourOuvre(aujourdhui: string, jours: number): string {
  const jour = ajouterJours(aujourdhui, jours);
  if (jours === 0 || new Date(`${jour}T12:00:00Z`).getUTCDay() !== 0) return jour;
  return ajouterJours(jour, jours < 0 ? -1 : 1);
}

/** Le decalage d'un fuseau a un instant, en ms : l'heure murale lue comme UTC, moins l'instant. */
function decalageDuFuseau(instant: number, fuseau: string): number {
  const parties = new Intl.DateTimeFormat('en-GB', {
    timeZone: fuseau, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(instant));
  const lire = (type: string): number => Number(parties.find((p) => p.type === type)?.value);
  return Date.UTC(lire('year'), lire('month') - 1, lire('day'), lire('hour'), lire('minute'), lire('second')) - Math.floor(instant / 1000) * 1000;
}

/** L'INSTANT absolu de « ce jour, a cette heure murale, dans ce fuseau ». */
export function instantLocal(jour: string, heure: number, minute: number, fuseau: string): Date {
  const [annee, mois, numero] = jour.split('-').map(Number) as [number, number, number];
  const mural = Date.UTC(annee, mois - 1, numero, heure, minute);
  const premier = mural - decalageDuFuseau(mural, fuseau);
  return new Date(mural - decalageDuFuseau(premier, fuseau));
}

/** Les delais de rappel du champ `reminderDelay`, en mois et jours. */
const DELAIS: Readonly<Record<string, { mois?: number; jours?: number }>> = {
  '3 semaines': { jours: 21 },
  '1 mois': { mois: 1 },
  '3 mois': { mois: 3 },
  '6 mois': { mois: 6 },
  '1 an': { mois: 12 },
  '3 ans': { mois: 36 },
};

/** La date de rappel d'un soin fait le `jour` avec un produit au delai `delai`. */
export function dateDeRappel(jour: string, delai: string): string | undefined {
  const d = DELAIS[delai];
  if (d === undefined) return undefined;
  const [annee, mois0, numero] = jour.split('-').map(Number) as [number, number, number];
  const mois = mois0 - 1 + (d.mois ?? 0);
  const dernier = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate();
  return ajouterJours(new Date(Date.UTC(annee, mois, Math.min(numero, dernier))).toISOString().slice(0, 10), d.jours ?? 0);
}

/** Un numero de lot stable : le code du produit et trois chiffres tires de la visite. */
export function lotDe(sku: string, demoKey: string): string {
  let somme = 0;
  for (const lettre of demoKey) somme = (somme * 31 + lettre.charCodeAt(0)) % 900;
  return `${sku.replace('-', '')}-${100 + somme}`;
}

/** Un sac sans clef vide : un champ sans valeur s'OMET. */
function sans<T extends Record<string, unknown>>(sac: T): CustomFieldValues {
  return Object.fromEntries(Object.entries(sac).filter(([, valeur]) => valeur !== undefined)) as CustomFieldValues;
}

/* ------------------------------------------------------------------ */
/* Les soins d'une visite                                              */
/* ------------------------------------------------------------------ */

const PRODUITS_PAR_CLEF = new Map(DEMO_PRODUITS.map((produit) => [produit.demoKey, produit]));
const PROTOCOLES_PAR_CLEF = new Map(DEMO_PROTOCOLES.map((protocole) => [protocole.demoKey, protocole]));

export interface SoinAPoser {
  produit: string;
  quantite: number;
  protocole?: string;
  rappelJours?: number;
}

/** Les lignes de soin d'une visite : un protocole se deplie en une ligne par ligne du protocole. */
export function soinsDeLaVisite(soins: ReadonlyArray<DemoSoin> | undefined): SoinAPoser[] {
  return (soins ?? []).flatMap((soin): SoinAPoser[] => {
    if ('produit' in soin) {
      return [{ produit: soin.produit, quantite: soin.quantite ?? 1, ...(soin.rappelJours === undefined ? {} : { rappelJours: soin.rappelJours }) }];
    }
    const protocole = PROTOCOLES_PAR_CLEF.get(soin.protocole);
    return (protocole?.lignes ?? []).map((ligne) => ({
      produit: ligne.produit,
      quantite: ligne.quantite,
      protocole: soin.protocole,
      ...(soin.rappelJours === undefined ? {} : { rappelJours: soin.rappelJours }),
    }));
  });
}

/* ------------------------------------------------------------------ */
/* L'import                                                            */
/* ------------------------------------------------------------------ */

/**
 * LES MODELES DE CONSULTATION de la demo : ce que « Creer depuis un modele »,
 * sur la porte Planning, propose. Un nom, un motif (valeur STOCKEE du
 * catalogue), et des lignes de soin : la `demoKey` d'un produit du Catalogue et
 * la quantite. Un modele n'a pas de sac ou poser une `demoKey` : il se reconnait
 * a son nom (famille `careRecords`, sous-type `consultation`).
 */
export interface DemoModele {
  name: string;
  description: string;
  reason: string;
  lignes: ReadonlyArray<{ produit: string; quantite: number }>;
}

export const DEMO_MODELES: ReadonlyArray<DemoModele> = [
  {
    name: 'Première visite chiot',
    description: 'Examen, premier vaccin et vermifuge.',
    reason: 'Vaccination',
    lignes: [
      { produit: 'pa-r10', quantite: 1 },
      { produit: 'pa-r1', quantite: 1 },
      { produit: 'pa-r6', quantite: 1 },
    ],
  },
  {
    name: 'Vaccin annuel chat',
    description: 'Rappel typhus-coryza et rage.',
    reason: 'Vaccination',
    lignes: [
      { produit: 'pa-r10', quantite: 1 },
      { produit: 'pa-r3', quantite: 1 },
      { produit: 'pa-r2', quantite: 1 },
    ],
  },
];

/** Les `demoKey` deja en base pour une liste de fiches, avec leur id. */
function idsParClef(fiches: ReadonlyArray<{ id: string; customFields?: Record<string, unknown> }>): Map<string, string> {
  const table = new Map<string, string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') table.set(clef, fiche.id);
  }
  return table;
}

const lignes = (n: number): string => `${n} ${n > 1 ? 'lignes' : 'ligne'}`;

/**
 * LE POIDS ACTUEL DE CHAQUE ANIMAL : sa DERNIERE pesee (constante `weight` de
 * l'examen de sa visite la plus recente), par `demoKey` d'animal. La fiche de
 * l'animal (`weightKg`, demo de Patients) et la courbe du dossier disent ainsi
 * le meme chiffre : l'import le recopie sur la fiche (`importDemoDataset`).
 */
export function poidsActuels(visites: ReadonlyArray<DemoConsultation> = DEMO_CONSULTATIONS): Map<string, number> {
  const rang = (v: DemoConsultation): number => v.jour * 10_000 + v.heure * 100 + v.minute;
  const derniere = new Map<string, DemoConsultation>();
  for (const visite of visites) {
    if (visite.examen?.mesures['weight'] === undefined) continue;
    const vue = derniere.get(visite.animal);
    if (vue === undefined || rang(visite) > rang(vue)) derniere.set(visite.animal, visite);
  }
  return new Map([...derniere].map(([animal, visite]) => [animal, visite.examen?.mesures['weight'] as number]));
}

/**
 * ECRIRE LE JEU DANS L'ORGANISATION COURANTE, et ne creer que ce qui manque.
 * `personneConnectee` est le `personId` de celle qui clique « Importer » (le
 * contexte du bouton le donne : `moi`) ; sans lui, le compte de l'emulateur local.
 * Chaque fiche enfant (soin, examen, constante, examen complementaire, tache)
 * a sa propre `demoKey` : une visite interrompue se complete a l'import
 * suivant, sans rien doubler.
 */
export async function importDemoDataset(sdk: TakiSdk, orgId: string, personneConnectee?: string | null): Promise<DemoImportResult> {
  const fuseau = sdk.timezone ?? FUSEAU_PAR_DEFAUT;
  const aujourdhui = jourDansFuseau(new Date(), fuseau);

  const fichesAnimaux = await sdk.assets.list({ type: 'animal', archived: 'all' });
  const animaux = idsParClef(fichesAnimaux);
  const especes = new Map(fichesAnimaux.map((fiche) => [fiche.id, fiche.customFields?.['species']]));
  const produits = idsParClef(await sdk.products.list({ type: 'article', archived: 'all' }));
  if (animaux.size === 0 || produits.size === 0) {
    const manque = animaux.size === 0 && produits.size === 0 ? 'du Catalogue et de Patients' : produits.size === 0 ? 'du Catalogue' : 'de Patients';
    return { created: 0, skipped: 0, message: `Rien n'a été créé. Importez d'abord les démonstrations ${manque}, puis celle-ci : ses produits et ses animaux.` };
  }
  const protocoles = idsParClef(await sdk.products.list({ type: 'protocole', archived: 'all' }));

  /* LES PERSONNES : moi (celle qui ouvre l'app) et un collegue. Sans le compte de
     l'emulateur, la premiere personne connectee fait moi, une autre le collegue. */
  const personnes = await sdk.personnes.list({ archived: 'all' });
  const parAdresse = new Map(personnes.map((p) => [p.email.toLowerCase(), p.personId]));
  const moi = personneConnectee ?? parAdresse.get(MOI) ?? personnes.find((p) => p.userId !== null)?.personId ?? personnes[0]?.personId;
  const collegue = parAdresse.get(COLLEGUE) ?? personnes.find((p) => p.personId !== moi)?.personId ?? moi;
  const personneDe = (vet: Veterinaire): string | undefined => (vet === 'moi' ? moi : collegue);

  const consultations = idsParClef(await sdk.careRecords.list({ type: TYPE_CONSULTATION, archived: 'all' }));
  const soinsConnus = idsParClef(await sdk.careRecords.list({ type: TYPE_SOIN, archived: 'all' }));
  const examens = idsParClef(await sdk.inspections.list({ type: 'examen', archived: 'all' }));
  const complementaires = idsParClef(await sdk.inspections.list({ type: 'complementaire', archived: 'all' }));
  const constantes = idsParClef(await sdk.measurements.list({ type: 'constante', archived: 'all' }));
  const taches = idsParClef(await sdk.todos.list({ type: TYPE_TACHE, archived: 'all' }));

  let created = 0;
  let skipped = 0;
  const horsAnimal: string[] = [];

  /* AUCUNE HEURE PASSEE DANS LE FUTUR DE L'IMPORT : les heures du jour sont
     recalees sur l'instant de l'import (`heureDuJour`). */
  const maintenant = new Date();
  const heures = heuresDuJour(DEMO_CONSULTATIONS, aujourdhui, fuseau, maintenant);

  for (const item of DEMO_CONSULTATIONS) {
    const assetId = animaux.get(item.animal);
    if (assetId === undefined) {
      horsAnimal.push(item.animal);
      skipped += 1;
      continue;
    }
    const jour = jourOuvre(aujourdhui, item.jour);
    const quand = heures.get(item.demoKey) ?? instantLocal(jour, item.heure, item.minute, fuseau);
    const veterinarian = personneDe(item.vet);
    const cloturee = item.stage === 'payee' || item.stage === 'aFacturer';

    /* ---- La consultation ---- */
    let consultationId = consultations.get(item.demoKey);
    if (consultationId === undefined) {
      const consultation = await sdk.careRecords.create({
        orgId,
        type: TYPE_CONSULTATION,
        assetId,
        performedAt: quand,
        customFields: sans({
          reason: item.reason,
          stage: item.stage,
          veterinarian,
          description: item.description,
          ...item.compteRendu,
          ...(cloturee ? { closedAt: clotureDe(quand, maintenant).toISOString(), closedBy: veterinarian } : {}),
          demoKey: item.demoKey,
        }),
      });
      consultationId = consultation.id;
      consultations.set(item.demoKey, consultationId);
      created += 1;
    } else {
      skipped += 1;
    }
    const lien = consultationId;

    /* ---- Les soins ---- */
    const facturation = item.stage === 'payee' ? 'facture' : 'aFacturer';
    const ecritures: Array<Promise<void>> = [];
    for (const [rang, soin] of soinsDeLaVisite(item.soins).entries()) {
      const demoKey = `${item.demoKey}-s${rang + 1}`;
      const productId = produits.get(soin.produit);
      if (soinsConnus.has(demoKey) || productId === undefined) {
        skipped += 1;
        continue;
      }
      const produit = PRODUITS_PAR_CLEF.get(soin.produit);
      const delai = produit?.prevention?.rappel;
      const rappel =
        delai === undefined
          ? undefined
          : soin.rappelJours !== undefined && produit?.category === 'Vaccin'
            ? ajouterJours(jour, soin.rappelJours)
            : dateDeRappel(jour, delai);
      ecritures.push(
        sdk.careRecords
          .create({
            orgId,
            type: TYPE_SOIN,
            assetId,
            customFields: sans({
              productId,
              consultationId: lien,
              careDate: jour,
              quantity: soin.quantite,
              protocolId: soin.protocole === undefined ? undefined : protocoles.get(soin.protocole),
              reminderDate: rappel,
              lotNumber: produit?.category === 'Vaccin' ? lotDe(produit.sku, item.demoKey) : undefined,
              billingStatus: facturation,
              demoKey,
            }),
          })
          .then(() => {
            created += 1;
          }),
      );
    }

    /* ---- L'examen clinique, puis ses constantes ---- */
    if (item.examen !== undefined) {
      const enListe = ESPECES_EN_LISTE.includes(String(especes.get(assetId)));
      const cleExamen = `${item.demoKey}-e`;
      let inspectionId = examens.get(cleExamen);
      if (inspectionId === undefined) {
        const points = enListe ? pointsDe(item.examen) : [];
        const inspection = await sdk.inspections.create({
          orgId,
          type: 'examen',
          assetId,
          performedAt: quand,
          ...(enListe ? { checkpoints: points.map((p) => ({ ...p })), verdict: verdictDe(points) } : {}),
          customFields: sans({ consultationId: lien, examinePar: veterinarian, mode: enListe ? 'liste' : 'texte', demoKey: cleExamen }),
        });
        inspectionId = inspection.id;
        examens.set(cleExamen, inspectionId);
        created += 1;
      } else {
        skipped += 1;
      }
      const inspection = inspectionId;
      for (const [metric, value] of Object.entries(item.examen.mesures)) {
        const demoKey = `${item.demoKey}-m-${metric}`;
        if (constantes.has(demoKey)) {
          skipped += 1;
          continue;
        }
        const unit = UNITES_DES_CONSTANTES[metric];
        ecritures.push(
          sdk.measurements
            .create({
              orgId,
              type: 'constante',
              assetId,
              careRecordId: lien,
              inspectionId: inspection,
              metric,
              value,
              ...(unit === undefined ? {} : { unit }),
              measuredAt: quand,
              customFields: { demoKey },
            })
            .then(() => {
              created += 1;
            }),
        );
      }
    }

    /* ---- Les examens complementaires cochés dans le compte rendu ---- */
    const coches = item.compteRendu?.['extraExams'];
    for (const [rang, examen] of (Array.isArray(coches) ? (coches as string[]) : []).entries()) {
      const demoKey = `${item.demoKey}-x${rang + 1}`;
      if (complementaires.has(demoKey)) {
        skipped += 1;
        continue;
      }
      ecritures.push(
        sdk.inspections
          .create({
            orgId,
            type: 'complementaire',
            assetId,
            performedAt: quand,
            customFields: sans({ consultationId: lien, examen, conclusion: item.conclusions?.[examen], demoKey }),
          })
          .then(() => {
            created += 1;
          }),
      );
    }
    await Promise.all(ecritures);
  }

  /* ---- Le poids de la fiche de chaque animal = sa derniere pesee (une seule verite) ---- */
  let alignes = 0;
  for (const [animal, kg] of poidsActuels()) {
    const assetId = animaux.get(animal);
    const actuel = fichesAnimaux.find((fiche) => fiche.id === assetId)?.customFields?.['weightKg'];
    if (assetId === undefined || actuel === kg) continue;
    await sdk.assets.update(assetId, { customFields: { weightKg: kg } });
    alignes += 1;
  }

  /* ---- Les taches des dossiers (todos::dossier) ---- */
  for (const item of DEMO_TACHES) {
    const consultationId = consultations.get(item.consultation);
    const animalId = animaux.get(item.animal);
    if (taches.has(item.demoKey) || animalId === undefined) {
      skipped += 1;
      continue;
    }
    await sdk.todos.create({
      orgId,
      type: TYPE_TACHE,
      status: item.status,
      dueAt: instantLocal(ajouterJours(aujourdhui, item.echeance), 12, 0, fuseau),
      customFields: sans({
        libelle: item.libelle,
        assigne: personneDe(item.vet),
        animalId,
        consultationId,
        demoKey: item.demoKey,
      }),
    });
    created += 1;
  }

  /* ---- Les modeles de consultation (templates::careRecords, sous-type consultation) ---- */
  const modelesConnus = new Set(
    (await sdk.templates.list({ type: 'careRecords', archived: 'all' })).filter((m) => m.targetType === TYPE_CONSULTATION).map((m) => m.name),
  );
  for (const modele of DEMO_MODELES) {
    const soinsDuModele = modele.lignes.flatMap((ligne) => {
      const productId = produits.get(ligne.produit);
      return productId === undefined ? [] : [{ productId, quantity: ligne.quantite }];
    });
    if (modelesConnus.has(modele.name) || soinsDuModele.length === 0) {
      skipped += 1;
      continue;
    }
    await sdk.templates.create({
      orgId,
      type: 'careRecords',
      targetType: TYPE_CONSULTATION,
      name: modele.name,
      description: modele.description,
      fields: { customFields: { reason: modele.reason } },
      children: soinsDuModele.map((soin) => ({
        family: 'careRecords',
        type: TYPE_SOIN,
        linkKey: 'customFields.consultationId',
        fields: { customFields: { ...soin, billingStatus: 'aFacturer' } },
      })),
    });
    modelesConnus.add(modele.name);
    created += 1;
  }

  const dejaLa = skipped === 0 ? "rien n'existait déjà" : `${skipped} déjà ${skipped > 1 ? 'présentes' : 'présente'}`;
  const absents = horsAnimal.length === 0 ? '' : ` ${new Set(horsAnimal).size} animaux de la démonstration de Patients manquent : importez-la, puis celle-ci à nouveau.`;
  const poids = alignes === 0 ? '' : ` Le poids de ${alignes} ${alignes > 1 ? 'animaux' : 'animal'} suit maintenant sa dernière pesée.`;
  const message =
    (created === 0
      ? `Le jeu de démonstration est déjà en place (${lignes(skipped)}).`
      : `${lignes(created)} ${created > 1 ? 'créées' : 'créée'}, ${dejaLa}.`) +
    poids +
    absents;
  return { created, skipped, message };
}
